import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ContactListItem, ConversationEvent } from '@callhub/shared';
import { Button, Input, TextArea } from '@/components/ui-next';
import { api } from '@/lib/api';
import { normalizePhone } from '@/lib/phone';
import { buildDialerPath } from '@/lib/routes';
import { CrmContactsPage } from './CrmContactsPage';

const CRM_V2_ENABLED = (import.meta.env.VITE_CRM_V2_ENABLED ?? '').toLowerCase() === 'true';

function formatEventTitle(event: ConversationEvent): string {
  const fallback = event.body?.trim();
  if (fallback) return fallback;

  switch (event.eventType) {
    case 'sms_inbound':
      return 'Inbound SMS';
    case 'sms_outbound':
      return 'Outbound SMS';
    case 'call_started':
      return 'Call started';
    case 'call_ended':
      return 'Call ended';
    case 'summary':
      return 'AI summary generated';
    case 'highlight':
      return 'Conversation highlight';
    case 'action_item':
      return 'Action item added';
    case 'voice_transcript':
      return 'Voice transcript segment';
    default:
      return event.eventType;
  }
}

export function ContactsPage() {
  if (CRM_V2_ENABLED) {
    return <CrmContactsPage />;
  }

  return <LegacyContactsPage />;
}

function LegacyContactsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const selectedContactId = searchParams.get('contact');

  const [newPhone, setNewPhone] = useState('');
  const [newName, setNewName] = useState('');

  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');

  const normalizedCreatePhone = useMemo(() => normalizePhone(newPhone), [newPhone]);
  const normalizedEditPhone = useMemo(() => normalizePhone(phoneNumber), [phoneNumber]);

  const setSelectedContactId = useCallback(
    (contactId: string | null, replace: boolean = false) => {
      const next = new URLSearchParams(searchParams);
      if (contactId) {
        next.set('contact', contactId);
      } else {
        next.delete('contact');
      }
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams],
  );

  const contactsQuery = useQuery({
    queryKey: ['contacts', debouncedSearchTerm],
    queryFn: async () => {
      const res = await api.getContacts(debouncedSearchTerm);
      return res.data;
    },
  });

  const contacts = useMemo(() => contactsQuery.data ?? [], [contactsQuery.data]);

  useEffect(() => {
    if (contactsQuery.isLoading) return;
    if (!contacts.length) {
      setSelectedContactId(null, true);
      return;
    }

    const hasSelected = contacts.some((contact) => contact.id === selectedContactId);
    if (!hasSelected) {
      setSelectedContactId(contacts[0].id, true);
    }
  }, [contacts, contactsQuery.isLoading, selectedContactId, setSelectedContactId]);

  const selectedContact = useMemo<ContactListItem | null>(
    () => contacts.find((contact) => contact.id === selectedContactId) ?? null,
    [contacts, selectedContactId],
  );

  useEffect(() => {
    if (!selectedContact) {
      setDisplayName('');
      setPhoneNumber('');
      setEmail('');
      setNotes('');
      return;
    }

    setDisplayName(selectedContact.displayName ?? '');
    setPhoneNumber(selectedContact.phoneNumber);
    setEmail(selectedContact.email ?? '');
    setNotes(selectedContact.notes ?? '');
  }, [selectedContact]);

  const timelineQuery = useQuery({
    queryKey: ['contact-timeline', selectedContactId],
    enabled: !!selectedContactId,
    queryFn: async () => {
      const res = await api.getContactTimeline(selectedContactId!);
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!normalizedCreatePhone) {
        throw new Error(
          'Invalid phone number format. Use E.164 (+14155552671) or a US 10-digit number.',
        );
      }

      return api.createContact({
        phoneNumber: normalizedCreatePhone,
        displayName: newName.trim() || undefined,
      });
    },
    onSuccess: async ({ data }) => {
      setNewPhone('');
      setNewName('');
      await queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setSelectedContactId(data.id);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedContactId) throw new Error('No selected contact');
      if (!normalizedEditPhone) {
        throw new Error(
          'Invalid phone number format. Use E.164 (+14155552671) or a US 10-digit number.',
        );
      }

      return api.updateContact(selectedContactId, {
        displayName,
        phoneNumber: normalizedEditPhone,
        email,
        notes,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['contact-timeline', selectedContactId] }),
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
      ]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (contactId: string) => api.deleteContact(contactId),
    onSuccess: async (_, contactId) => {
      if (selectedContactId === contactId) {
        setSelectedContactId(null, true);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['contact-timeline', contactId] }),
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
      ]);
    },
  });

  const messageMutation = useMutation({
    mutationFn: async (payload: { contactId: string; phoneNumber: string }) => {
      const res = await api.getConversationByPhone(payload.phoneNumber);
      return res.data.conversation.id;
    },
    onSuccess: async (conversationId, payload) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
      ]);
      navigate(
        buildDialerPath({
          conversation: conversationId,
          contact: payload.contactId,
          phone: payload.phoneNumber,
        }),
      );
    },
  });

  const handleCreateContact = () => {
    if (!newPhone.trim()) return;
    createMutation.mutate();
  };

  const handleSaveContact = () => {
    if (!selectedContactId) return;
    updateMutation.mutate();
  };

  const handleDeleteContact = () => {
    if (!selectedContact) return;
    const label = selectedContact.displayName || selectedContact.phoneNumber;
    const shouldDelete = window.confirm(
      `Delete contact "${label}"? This removes the contact card but keeps conversation history.`,
    );
    if (!shouldDelete) return;
    deleteMutation.mutate(selectedContact.id);
  };

  const handleMessageContact = () => {
    if (!selectedContact) return;

    if (selectedContact.openConversationId) {
      navigate(
        buildDialerPath({
          conversation: selectedContact.openConversationId,
          contact: selectedContact.id,
          phone: selectedContact.phoneNumber,
        }),
      );
      return;
    }

    messageMutation.mutate({
      contactId: selectedContact.id,
      phoneNumber: selectedContact.phoneNumber,
    });
  };

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Contacts</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage customer contacts and SMS timeline
          </p>
        </div>

        <div className="flex w-full max-w-xl flex-wrap gap-2 rounded-lg border border-border bg-card p-3">
          <Input
            value={newPhone}
            onChange={(event) => {
              setNewPhone(event.target.value);
              if (createMutation.isError) createMutation.reset();
            }}
            onBlur={() => {
              const normalized = normalizePhone(newPhone);
              if (normalized) setNewPhone(normalized);
            }}
            placeholder="+1234567890"
            className="min-w-[12rem] flex-1 bg-background"
          />
          <Input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder="Display name (optional)"
            className="min-w-[12rem] flex-1 bg-background"
          />
          <Button
            variant="primary"
            onClick={handleCreateContact}
            isDisabled={!normalizedCreatePhone || createMutation.isPending}
          >
            {createMutation.isPending ? 'Adding...' : 'Add Contact'}
          </Button>
          {newPhone.trim() && !normalizedCreatePhone ? (
            <p className="w-full text-xs text-amber-700 dark:text-amber-300">
              Use E.164 (+14155552671) or a full 10-digit US number.
            </p>
          ) : null}
          {createMutation.isError ? (
            <p className="w-full text-sm text-red-400">{(createMutation.error as Error).message}</p>
          ) : null}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4 lg:col-span-1">
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search contacts..."
            className="mb-4 bg-background"
          />

          <div className="space-y-2">
            {contactsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading contacts...</p>
            ) : contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No contacts found.</p>
            ) : (
              contacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => setSelectedContactId(contact.id)}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${
                    selectedContactId === contact.id
                      ? 'border-brand-500/50 bg-brand-500/10'
                      : 'border-border bg-background hover:border-brand-500/40'
                  }`}
                >
                  <p className="truncate text-sm font-medium text-foreground">
                    {contact.displayName || contact.phoneNumber}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{contact.phoneNumber}</p>
                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    {contact.lastInteractionPreview || 'No recent interactions'}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2">
          {!selectedContact ? (
            <p className="text-sm text-muted-foreground">
              Select a contact to view details and timeline.
            </p>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-border pb-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {selectedContact.displayName || selectedContact.phoneNumber}
                  </h3>
                  <p className="text-sm text-muted-foreground">{selectedContact.phoneNumber}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    onClick={handleMessageContact}
                    isDisabled={messageMutation.isPending}
                  >
                    {messageMutation.isPending ? 'Opening...' : 'Message'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      navigate(
                        buildDialerPath({
                          phone: selectedContact.phoneNumber,
                          contact: selectedContact.id,
                        }),
                      );
                    }}
                    isDisabled={messageMutation.isPending}
                  >
                    Open in Dialer
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (!selectedContact.openConversationId) return;
                      navigate(
                        buildDialerPath({
                          conversation: selectedContact.openConversationId,
                          contact: selectedContact.id,
                          phone: selectedContact.phoneNumber,
                        }),
                      );
                    }}
                    isDisabled={!selectedContact.openConversationId || messageMutation.isPending}
                  >
                    Open in Comms
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDeleteContact}
                    isDisabled={
                      deleteMutation.isPending ||
                      messageMutation.isPending ||
                      updateMutation.isPending
                    }
                    className="text-red-600 hover:text-red-500 dark:text-red-300 dark:hover:text-red-200"
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
              {messageMutation.isError ? (
                <p className="mb-3 text-sm text-red-400">
                  {(messageMutation.error as Error).message}
                </p>
              ) : null}
              {deleteMutation.isError ? (
                <p className="mb-3 text-sm text-red-400">
                  {(deleteMutation.error as Error).message}
                </p>
              ) : null}

              <div className="grid gap-3 md:grid-cols-3">
                <Input
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  placeholder="Display Name"
                  className="bg-background"
                />
                <Input
                  value={phoneNumber}
                  onChange={(event) => setPhoneNumber(event.target.value)}
                  onBlur={() => {
                    const normalized = normalizePhone(phoneNumber);
                    if (normalized) setPhoneNumber(normalized);
                  }}
                  placeholder="Phone Number"
                  className="bg-background"
                />
                <Input
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Email"
                  className="bg-background"
                />
              </div>
              {phoneNumber.trim() && !normalizedEditPhone ? (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                  Use E.164 (+14155552671) or a full 10-digit US number.
                </p>
              ) : null}

              <TextArea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Notes"
                className="mt-3 bg-background"
              />

              <div className="mt-3 flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleSaveContact}
                  isDisabled={
                    updateMutation.isPending ||
                    deleteMutation.isPending ||
                    messageMutation.isPending ||
                    !normalizedEditPhone
                  }
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Contact'}
                </Button>
              </div>

              <div className="mt-6 border-t border-border pt-4">
                <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Timeline
                </h4>

                <div className="space-y-3">
                  {timelineQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Loading timeline...</p>
                  ) : (timelineQuery.data ?? []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No timeline events yet.</p>
                  ) : (
                    (timelineQuery.data ?? []).map((event) => (
                      <div
                        key={event.id}
                        className="rounded-lg border border-border bg-background px-3 py-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm text-foreground">{formatEventTitle(event)}</p>
                          <span className="text-xs uppercase text-muted-foreground">
                            {event.channel}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {new Date(event.createdAt).toLocaleString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
